import { ApiService, type ApiRequestConfig } from "./api.service";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import type { components } from "@/types/api.generated";

export type TProgramRestrictedProjects = components["schemas"]["ProgramRestrictedProjects"];
import type {
  TGoal,
  TGoalCreatePayload,
  TGoalLeadBinding,
  TGoalLinkedItem,
  TGoalLinkedRequest,
  TGoalMetric,
  TGoalMetricCreatePayload,
  TGoalMetricDatapoint,
  TGoalMetricDatapointCreatePayload,
  TGoalMetricDatapointUpdatePayload,
  TGoalMetricUpdatePayload,
  TGoalUpdatePayload,
  TPartialProject,
  TProgramLinkedRequest,
  TProgramRequestCandidatePage,
  TProcess,
  TProcessStage,
  TProcessUsage,
  TProgram,
  TProgramHealth,
  TProgramCreatePayload,
  TProgramUpdatePayload,
  TProject,
  TProjectActivityPage,
  TProjectComment,
  TProjectCommentPage,
  TProjectSubmissionLink,
} from "@/types/project";

/** Default page size for the project activity + comments feeds. Mirrors the
 *  backend ``DEFAULT_FEED_PAGE_SIZE``. */
const PROJECT_FEED_PAGE_SIZE = 50;

const base = (slug: string) => `/api/workspaces/${slug}/programs`;

export class ProgramsService extends ApiService {
  /** How many Team restricted projects the program contains (NG-2965), for the
   *  warning before its Owner changes. `applies` is false with the flag off. */
  getRestrictedProjects(slug: string, programId: string): Promise<TProgramRestrictedProjects> {
    return this.get<TProgramRestrictedProjects>(`${base(slug)}/${programId}/restricted-projects/`);
  }

  list(
    slug: string,
    includeArchived = false,
    options: { visibility?: "selection" } = {},
    config?: ApiRequestConfig,
  ): Promise<TProgram[]> {
    // Server-paginated (issue #1254); walk the pages so the Programs table keeps
    // the full set it renders/filters locally. UI is unchanged.
    // `visibility=selection` (#2792) is the picker mode used by the
    // Requests/Projects program dropdowns — strict team-family view scope, no
    // owner/led/admin see-all. The default (omitted) is the full list view.
    return this.getAllPages<TProgram>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (includeArchived) qs.set("include_archived", "true");
        if (options.visibility) qs.set("visibility", options.visibility);
        if (cursor) qs.set("cursor", cursor);
        return `${base(slug)}/?${qs}`;
      },
      undefined,
      config,
    );
  }

  /**
   * Export the Programs list as CSV, scoped to the same filters the user sees
   * (`options`) and, when `body.ids` is set, restricted to those programs.
   */
  exportPrograms(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    options: {
      q?: string;
      status?: string[];
      include_archived?: boolean;
      goals?: string[];
      home_team?: string[];
    } = {},
  ): Promise<Response> {
    const params = new URLSearchParams();
    if (options.q) params.set("q", options.q);
    if (options.include_archived) params.set("include_archived", "true");
    for (const s of options.status ?? []) params.append("status", s);
    // Repeated `goals=<id>` — the same shape the list endpoint takes, matching
    // ANY of the named goals (NG-619).
    for (const id of options.goals ?? []) params.append("goals", id);
    // Repeated `home_team=<id>` (NG-2163): the server matches each program's
    // scalar Home, so the file holds exactly the rows the table's filter shows.
    for (const id of options.home_team ?? []) params.append("home_team", id);
    const qs = params.toString();
    return this.postRaw(`/api/workspaces/${slug}/export-programs/${qs ? `?${qs}` : ""}`, body);
  }

  getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  create(slug: string, data: TProgramCreatePayload): Promise<TProgram> {
    return this.post(`${base(slug)}/`, data);
  }

  retrieve(slug: string, programId: string, config?: ApiRequestConfig): Promise<TProgram> {
    return this.get(`${base(slug)}/${programId}/`, config);
  }

  update(slug: string, programId: string, data: TProgramUpdatePayload): Promise<TProgram> {
    return this.patch(`${base(slug)}/${programId}/`, data);
  }

  destroy(slug: string, programId: string): Promise<void> {
    return this.delete(`${base(slug)}/${programId}/`);
  }

  /** A program's linked requests (NG-1630), whether they picked it at intake
   *  or were added from the program page (NG-1734). Fenced server-side to what
   *  the caller may read, so a short list is a permission answer rather than a
   *  bug. */
  listRequests(
    slug: string,
    programId: string,
    config?: ApiRequestConfig,
  ): Promise<TProgramLinkedRequest[]> {
    return this.get(`${base(slug)}/${programId}/requests/`, config);
  }

  /** Link an existing request to this program. Takes `request.edit` on the
   *  TARGET request server-side, not `program.edit`. Returns the created row in
   *  the list shape, so the caller can append without a refetch. */
  linkRequestToProgram(
    slug: string,
    programId: string,
    submissionId: string,
  ): Promise<TProgramLinkedRequest> {
    return this.post(`${base(slug)}/${programId}/requests/`, { submission_id: submissionId });
  }

  /** Search for requests to add to this program (NG-1734): the picker's own
   *  narrow read, rather than the full Requests list it used to call. Matches
   *  the request name, the form name, and an identifier (`R-284` or `284`). */
  searchRequestCandidates(
    slug: string,
    programId: string,
    q: string,
  ): Promise<TProgramRequestCandidatePage> {
    const query = q ? `?${new URLSearchParams({ q }).toString()}` : "";
    return this.get(`${base(slug)}/${programId}/request-candidates/${query}`);
  }

  unlinkRequestFromProgram(slug: string, programId: string, submissionId: string): Promise<void> {
    return this.delete(`${base(slug)}/${programId}/requests/${submissionId}/`);
  }

  listProjects(
    slug: string,
    programId: string,
    config?: ApiRequestConfig,
  ): Promise<TPartialProject[]> {
    // Server-paginated; walk the pages so the program overview table keeps the
    // full linked-project set it renders today.
    return this.getAllPages<TPartialProject>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (cursor) qs.set("cursor", cursor);
        return `${base(slug)}/${programId}/projects/?${qs}`;
      },
      undefined,
      config,
    );
  }

  addProjects(slug: string, programId: string, projectIds: string[]): Promise<TProgram> {
    return this.post(`${base(slug)}/${programId}/projects/`, { project_ids: projectIds });
  }

  removeProject(slug: string, programId: string, projectId: string): Promise<void> {
    return this.delete(`${base(slug)}/${programId}/projects/${projectId}/`);
  }

  archive(slug: string, programId: string): Promise<TProgram> {
    return this.post(`${base(slug)}/${programId}/archive/`, {});
  }

  unarchive(slug: string, programId: string): Promise<TProgram> {
    return this.delete(`${base(slug)}/${programId}/archive/`);
  }

  /** Project-health rollup for one program. Read only by the flag-off overview
   *  (NG-337 removes it with the flag). */
  getHealth(slug: string, programId: string, config?: ApiRequestConfig): Promise<TProgramHealth> {
    return this.get(`${base(slug)}/${programId}/health/`, config);
  }
}

export class GoalsService extends ApiService {
  async list(workspaceSlug: string, config?: ApiRequestConfig): Promise<TGoal[]> {
    // Server-paginated (issue #1254); walk the pages so the Goals list keeps the
    // full set it renders locally. UI is unchanged.
    return this.getAllPages<TGoal>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (cursor) qs.set("cursor", cursor);
        return `/api/workspaces/${workspaceSlug}/goals/?${qs}`;
      },
      undefined,
      config,
    );
  }

  create(workspaceSlug: string, payload: TGoalCreatePayload): Promise<TGoal> {
    return this.post(`/api/workspaces/${workspaceSlug}/goals/`, payload);
  }

  retrieve(workspaceSlug: string, goalId: string, config?: ApiRequestConfig): Promise<TGoal> {
    return this.get(`/api/workspaces/${workspaceSlug}/goals/${goalId}/`, config);
  }

  update(workspaceSlug: string, goalId: string, payload: TGoalUpdatePayload): Promise<TGoal> {
    return this.patch(`/api/workspaces/${workspaceSlug}/goals/${goalId}/`, payload);
  }

  deleteGoal(workspaceSlug: string, goalId: string): Promise<void> {
    return this.delete(`/api/workspaces/${workspaceSlug}/goals/${goalId}/`);
  }

  // --- Goal Leads (NG-616): direct `goal.lead` designations through the
  // members endpoint. ---

  listLeads(
    workspaceSlug: string,
    goalId: string,
    config?: ApiRequestConfig,
  ): Promise<TGoalLeadBinding[]> {
    return this.get(`/api/workspaces/${workspaceSlug}/goals/${goalId}/members/`, config);
  }

  addLead(workspaceSlug: string, goalId: string, memberId: string): Promise<TGoalLeadBinding> {
    return this.post(`/api/workspaces/${workspaceSlug}/goals/${goalId}/members/`, {
      member_id: memberId,
    });
  }

  removeLead(workspaceSlug: string, goalId: string, bindingId: string): Promise<void> {
    return this.delete(`/api/workspaces/${workspaceSlug}/goals/${goalId}/members/${bindingId}/`);
  }

  // --- Goal-side work links (NG-616): the reverse direction of the project /
  // program `goals` fields — same join rows, written from the goal page. ---

  listLinkedProjects(
    workspaceSlug: string,
    goalId: string,
    config?: ApiRequestConfig,
  ): Promise<TGoalLinkedItem[]> {
    return this.get(`/api/workspaces/${workspaceSlug}/goals/${goalId}/projects/`, config);
  }

  linkProject(workspaceSlug: string, goalId: string, projectId: string): Promise<TGoalLinkedItem> {
    return this.post(`/api/workspaces/${workspaceSlug}/goals/${goalId}/projects/`, {
      project_id: projectId,
    });
  }

  unlinkProject(workspaceSlug: string, goalId: string, projectId: string): Promise<void> {
    return this.delete(`/api/workspaces/${workspaceSlug}/goals/${goalId}/projects/${projectId}/`);
  }

  listLinkedPrograms(
    workspaceSlug: string,
    goalId: string,
    config?: ApiRequestConfig,
  ): Promise<TGoalLinkedItem[]> {
    return this.get(`/api/workspaces/${workspaceSlug}/goals/${goalId}/programs/`, config);
  }

  linkProgram(workspaceSlug: string, goalId: string, programId: string): Promise<TGoalLinkedItem> {
    return this.post(`/api/workspaces/${workspaceSlug}/goals/${goalId}/programs/`, {
      program_id: programId,
    });
  }

  unlinkProgram(workspaceSlug: string, goalId: string, programId: string): Promise<void> {
    return this.delete(`/api/workspaces/${workspaceSlug}/goals/${goalId}/programs/${programId}/`);
  }

  // Requests differ from the two above (NG-618): they carry an identifier
  // (``R-12``) and their name lives in the submission's answers, so the row
  // shape is wider — and the backend fences the list to the requests the caller
  // may already read, since requests have per-row privacy that programs don't.
  listLinkedRequests(
    workspaceSlug: string,
    goalId: string,
    config?: ApiRequestConfig,
  ): Promise<TGoalLinkedRequest[]> {
    return this.get(`/api/workspaces/${workspaceSlug}/goals/${goalId}/requests/`, config);
  }

  linkRequest(
    workspaceSlug: string,
    goalId: string,
    submissionId: string,
  ): Promise<TGoalLinkedRequest> {
    return this.post(`/api/workspaces/${workspaceSlug}/goals/${goalId}/requests/`, {
      submission_id: submissionId,
    });
  }

  unlinkRequest(workspaceSlug: string, goalId: string, submissionId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${workspaceSlug}/goals/${goalId}/requests/${submissionId}/`,
    );
  }

  createMetric(
    workspaceSlug: string,
    goalId: string,
    payload: TGoalMetricCreatePayload,
  ): Promise<TGoalMetric> {
    return this.post(`/api/workspaces/${workspaceSlug}/goals/${goalId}/metrics/`, payload);
  }

  updateMetric(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    payload: TGoalMetricUpdatePayload,
  ): Promise<TGoalMetric> {
    return this.patch(
      `/api/workspaces/${workspaceSlug}/goals/${goalId}/metrics/${metricId}/`,
      payload,
    );
  }

  deleteMetric(workspaceSlug: string, goalId: string, metricId: string): Promise<void> {
    return this.delete(`/api/workspaces/${workspaceSlug}/goals/${goalId}/metrics/${metricId}/`);
  }

  // --- Datapoints (NG-336): per-row CRUD by design, never list-replace — the
  // series is unbounded and append-dominated. ---

  listDatapoints(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    config?: ApiRequestConfig,
  ): Promise<TGoalMetricDatapoint[]> {
    // Server-paginated (unbounded series); walk the pages so the chart plots
    // the full timeline.
    return this.getAllPages<TGoalMetricDatapoint>(
      ({ cursor, perPage }) => {
        const qs = new URLSearchParams({ per_page: String(perPage) });
        if (cursor) qs.set("cursor", cursor);
        return `/api/workspaces/${workspaceSlug}/goals/${goalId}/metrics/${metricId}/datapoints/?${qs}`;
      },
      undefined,
      config,
    );
  }

  createDatapoint(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    payload: TGoalMetricDatapointCreatePayload,
  ): Promise<TGoalMetricDatapoint> {
    return this.post(
      `/api/workspaces/${workspaceSlug}/goals/${goalId}/metrics/${metricId}/datapoints/`,
      payload,
    );
  }

  updateDatapoint(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    datapointId: string,
    payload: TGoalMetricDatapointUpdatePayload,
  ): Promise<TGoalMetricDatapoint> {
    return this.patch(
      `/api/workspaces/${workspaceSlug}/goals/${goalId}/metrics/${metricId}/datapoints/${datapointId}/`,
      payload,
    );
  }

  deleteDatapoint(
    workspaceSlug: string,
    goalId: string,
    metricId: string,
    datapointId: string,
  ): Promise<void> {
    return this.delete(
      `/api/workspaces/${workspaceSlug}/goals/${goalId}/metrics/${metricId}/datapoints/${datapointId}/`,
    );
  }
}

export interface IProcessesPage {
  results: TProcess[];
  next_cursor: string;
  prev_cursor: string;
  next_page_results: boolean;
  prev_page_results: boolean;
  total_count: number;
  count: number;
}

export interface IProcessesListQuery {
  cursor?: string | null;
  q?: string;
  type?: string[];
  stages?: string[];
  /** Active/Inactive filter chips (NG-635) — `"active"` and/or `"inactive"`.
   *  Both values, like neither, filter nothing. Distinct from the server's
   *  `?active=true` BANK read: this is a screen's filter, that is the product
   *  rule that a retired process stops being offered for a new pick. */
  status?: string[];
  order_by?: string;
  per_page?: number;
}

/** A stage on a process-create payload, or on `addStage`. `order` is optional:
 *  the server appends after the process's current stages when it is omitted. */
export interface IProcessStagePayload {
  name: string;
  order?: number;
  /** Retire the stage, or bring it back (NG-635). Sent on its own — the server
   *  routes it through a use case that also re-derives every project's current
   *  stage, so it is never just another field on a rename. */
  is_active?: boolean;
}

export interface IProcessCreatePayload {
  name: string;
  description?: string;
  stages?: IProcessStagePayload[];
  /** Owning team ids. An EMPTY array is a real value — "All teams" — and is
   *  what a workspace admin sends for an unscoped process. Omitting the key
   *  entirely also means All teams on create. */
  team_access?: string[];
}

export type TProcessUpdatePayload = Partial<
  Pick<IProcessCreatePayload, "name" | "description" | "team_access">
> & {
  /** Retire the process, or bring it back (NG-635). An inactive process stays
   *  fully editable, so this travels alongside `name`/`description` rather than
   *  through a separate endpoint. */
  is_active?: boolean;
};

/** On UPDATE `team_access` has three distinct states and the caller must respect
 *  all three (NG-679): the key ABSENT leaves the links untouched, `[]` clears the
 *  process back to All teams, and a list replaces the set. An autosave must
 *  therefore never send the key it did not touch — echoing an unchanged value is
 *  harmless server-side (restated ids are exempt from validation) but sending `[]`
 *  because a field was not loaded would silently unscope the process. */

export class ProcessesService extends ApiService {
  async listPickable(
    workspaceSlug: string,
    config?: ApiRequestConfig,
  ): Promise<{ id: string; name: string }[]> {
    return this.get(`/api/workspaces/${workspaceSlug}/processes/pickable/`, config);
  }

  async list(workspaceSlug: string, config?: ApiRequestConfig): Promise<TProcess[]> {
    return this.get(`/api/workspaces/${workspaceSlug}/processes/`, config);
  }

  /** The stages of one project's process, gated on `project.read` (NG-2893):
   *  the same rows {@link list} embeds under that process, for any reader of
   *  the project, which the bank (`process.read`) is not. `[]` when the
   *  project has no process. */
  async listProjectStages(
    workspaceSlug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProcessStage[]> {
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/stage-options/`,
      config,
    );
  }

  async create(workspaceSlug: string, payload: IProcessCreatePayload): Promise<TProcess> {
    return this.post(`/api/workspaces/${workspaceSlug}/processes/`, payload);
  }

  async update(
    workspaceSlug: string,
    processId: string,
    payload: TProcessUpdatePayload,
  ): Promise<TProcess> {
    return this.patch(`/api/workspaces/${workspaceSlug}/processes/${processId}/`, payload);
  }

  async remove(workspaceSlug: string, processId: string): Promise<void> {
    return this.delete(`/api/workspaces/${workspaceSlug}/processes/${processId}/`);
  }

  async addStage(
    workspaceSlug: string,
    processId: string,
    payload: IProcessStagePayload,
  ): Promise<TProcessStage> {
    return this.post(`/api/workspaces/${workspaceSlug}/processes/${processId}/stages/`, payload);
  }

  async updateStage(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    payload: Partial<IProcessStagePayload>,
  ): Promise<TProcessStage> {
    return this.patch(
      `/api/workspaces/${workspaceSlug}/processes/${processId}/stages/${stageId}/`,
      payload,
    );
  }

  /**
   * Delete a stage, optionally moving the live work items in it to `moveTo`
   * first (NG-635).
   *
   * `moveTo` is required by the server whenever the stage holds anything —
   * without it the response is a 409 naming the candidates and which one to
   * pre-select, which is what the caller turns into the "move N work items to…"
   * dialog. Passing it for an EMPTY stage is harmless and deliberately allowed:
   * the stage can be emptied by another tab between the dialog opening and the
   * retry, and turning that race into an error the user cannot act on would be
   * worse than moving nothing.
   */
  async removeStage(
    workspaceSlug: string,
    processId: string,
    stageId: string,
    moveTo?: string,
  ): Promise<void> {
    const query = moveTo ? `?move_to=${encodeURIComponent(moveTo)}` : "";
    return this.delete(
      `/api/workspaces/${workspaceSlug}/processes/${processId}/stages/${stageId}/${query}`,
    );
  }

  /**
   * Replace the whole stage ordering in one write. Per-stage moves cannot
   * express a reorder: the server holds a unique active (process, order)
   * constraint, so any partial sequence collides mid-flight. `stageIds` must
   * therefore be every active stage of the process, exactly once. Answers with
   * the whole process, renumbered 1..n.
   */
  async reorderStages(
    workspaceSlug: string,
    processId: string,
    stageIds: string[],
  ): Promise<TProcess> {
    return this.put(`/api/workspaces/${workspaceSlug}/processes/${processId}/stages/order/`, {
      stage_ids: stageIds,
    });
  }

  /** Paginated processes fetch for the settings list (server filters/sort +
   *  cursor pagination). Distinct from ``list`` (the full bank that the
   *  template-form process pickers + work-item panels fetch). */
  async listProcessesPage(
    workspaceSlug: string,
    options: IProcessesListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<IProcessesPage> {
    const params = new URLSearchParams();
    params.set("per_page", String(options.per_page ?? 50));
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.q) params.set("q", options.q);
    if (options.order_by) params.set("order_by", options.order_by);
    for (const v of options.type ?? []) params.append("type", v);
    for (const s of options.stages ?? []) params.append("stages", s);
    for (const s of options.status ?? []) params.append("status", s);
    return this.get(`/api/workspaces/${workspaceSlug}/processes/?${params.toString()}`, config);
  }

  /**
   * Export the Processes list as CSV, scoped to the same filters/sort as the
   * list (`options`) and, when `body.ids` is set, restricted to those rows.
   */
  async exportProcesses(
    workspaceSlug: string,
    body: { ids?: string[]; columns?: string[] },
    options: {
      q?: string;
      type?: string[];
      stages?: string[];
      status?: string[];
      order_by?: string;
    } = {},
  ): Promise<Response> {
    const params = new URLSearchParams();
    if (options.q) params.set("q", options.q);
    if (options.order_by) params.set("order_by", options.order_by);
    for (const v of options.type ?? []) params.append("type", v);
    for (const s of options.stages ?? []) params.append("stages", s);
    for (const s of options.status ?? []) params.append("status", s);
    const qs = params.toString();
    return this.postRaw(
      `/api/workspaces/${workspaceSlug}/export-processes/${qs ? `?${qs}` : ""}`,
      body,
    );
  }

  async getExportStatus(workspaceSlug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${workspaceSlug}/export-status/${token}/`);
  }

  async retrieve(workspaceSlug: string, processId: string): Promise<TProcess> {
    return this.get(`/api/workspaces/${workspaceSlug}/processes/${processId}/`);
  }

  /** What still points at a process: projects + templates using it, and the
   *  work-item count of each of its stages. The editor reads this to decide
   *  which stage removals and which process delete it is allowed to offer. */
  async usage(
    workspaceSlug: string,
    processId: string,
    config?: ApiRequestConfig,
  ): Promise<TProcessUsage> {
    return this.get(`/api/workspaces/${workspaceSlug}/processes/${processId}/usage/`, config);
  }
}

export class ProjectActivityService extends ApiService {
  /** Fetch one keyset page of the activity feed (newest-first). Pass the prior
   *  page's ``next_cursor`` to load older rows. */
  async list(
    workspaceSlug: string,
    projectId: string,
    options: { cursor?: string | null; perPage?: number } = {},
    config?: ApiRequestConfig,
  ): Promise<TProjectActivityPage> {
    const params: Record<string, string | number> = {
      per_page: options.perPage ?? PROJECT_FEED_PAGE_SIZE,
    };
    if (options.cursor) params.cursor = options.cursor;
    return this.get(`/api/workspaces/${workspaceSlug}/projects/${projectId}/activities/`, {
      ...config,
      params,
    });
  }
}

export class ProjectCommentService extends ApiService {
  /** Fetch one keyset page of the comments feed (newest-first). Pass the prior
   *  page's ``next_cursor`` to load older comments. */
  async list(
    workspaceSlug: string,
    projectId: string,
    options: { cursor?: string | null; perPage?: number } = {},
    config?: ApiRequestConfig,
  ): Promise<TProjectCommentPage> {
    const params: Record<string, string | number> = {
      per_page: options.perPage ?? PROJECT_FEED_PAGE_SIZE,
    };
    if (options.cursor) params.cursor = options.cursor;
    return this.get(`/api/workspaces/${workspaceSlug}/projects/${projectId}/comments/`, {
      ...config,
      params,
    });
  }

  async create(
    workspaceSlug: string,
    projectId: string,
    data: Partial<TProjectComment>,
  ): Promise<TProjectComment> {
    return this.post(`/api/workspaces/${workspaceSlug}/projects/${projectId}/comments/`, data);
  }

  async update(
    workspaceSlug: string,
    projectId: string,
    commentId: string,
    data: Partial<TProjectComment>,
  ): Promise<TProjectComment> {
    return this.patch(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/comments/${commentId}/`,
      data,
    );
  }

  async remove(workspaceSlug: string, projectId: string, commentId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/comments/${commentId}/`,
    );
  }
}

export class ProgramActivityService extends ApiService {
  /** Fetch one keyset page of a program's activity feed (newest-first). Pass
   *  the prior page's ``next_cursor`` to load older rows. Shares the project
   *  feed page shape. */
  async list(
    workspaceSlug: string,
    programId: string,
    options: { cursor?: string | null; perPage?: number } = {},
    config?: ApiRequestConfig,
  ): Promise<TProjectActivityPage> {
    const params: Record<string, string | number> = {
      per_page: options.perPage ?? PROJECT_FEED_PAGE_SIZE,
    };
    if (options.cursor) params.cursor = options.cursor;
    return this.get(`/api/workspaces/${workspaceSlug}/programs/${programId}/activities/`, {
      ...config,
      params,
    });
  }
}

export class ProgramCommentService extends ApiService {
  /** Fetch one keyset page of a program's comments feed (newest-first). */
  async list(
    workspaceSlug: string,
    programId: string,
    options: { cursor?: string | null; perPage?: number } = {},
    config?: ApiRequestConfig,
  ): Promise<TProjectCommentPage> {
    const params: Record<string, string | number> = {
      per_page: options.perPage ?? PROJECT_FEED_PAGE_SIZE,
    };
    if (options.cursor) params.cursor = options.cursor;
    return this.get(`/api/workspaces/${workspaceSlug}/programs/${programId}/comments/`, {
      ...config,
      params,
    });
  }

  async create(
    workspaceSlug: string,
    programId: string,
    data: Partial<TProjectComment>,
  ): Promise<TProjectComment> {
    return this.post(`/api/workspaces/${workspaceSlug}/programs/${programId}/comments/`, data);
  }

  async remove(workspaceSlug: string, programId: string, commentId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${workspaceSlug}/programs/${programId}/comments/${commentId}/`,
    );
  }
}

export class ProjectLinkedSubmissionsService extends ApiService {
  async list(
    workspaceSlug: string,
    projectId: string,
    config?: ApiRequestConfig,
  ): Promise<TProjectSubmissionLink[]> {
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/linked-submissions/`,
      config,
    );
  }

  async link(
    workspaceSlug: string,
    projectId: string,
    submissionId: string,
  ): Promise<TProjectSubmissionLink> {
    return this.post(`/api/workspaces/${workspaceSlug}/projects/${projectId}/linked-submissions/`, {
      submission_id: submissionId,
    });
  }

  async unlink(workspaceSlug: string, projectId: string, submissionId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/linked-submissions/${submissionId}/`,
    );
  }
}

export class ProjectV3Service extends ApiService {
  async ackStageClearing(
    workspaceSlug: string,
    projectId: string,
  ): Promise<{ acknowledged: boolean }> {
    return this.post(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/ack-stage-clearing/`,
      {},
    );
  }

  async duplicate(workspaceSlug: string, projectId: string): Promise<TProject> {
    return this.post(`/api/workspaces/${workspaceSlug}/projects/${projectId}/duplicate/`, {});
  }
}
